import { supplierPriceListsApi } from '@/api';
import { queryKeys } from '@/api/query-keys';
import {
  keepPreviousData,
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { AxiosError } from 'axios';
import {
  IngestResponse,
  ItemsListResponse,
  ListFeedsFilters,
  ListItemsFilters,
  ListPriceHistoryFilters,
  ListRunsFilters,
  PriceHistoryResponse,
  RunReport,
  RunsListResponse,
  SupplierPriceListDetail,
  SupplierPriceListsListResponse,
} from './supplier-price-lists.types';

const ACTIVE_RUN_STATUSES = new Set([
  'pending',
  'fetching',
  'fetched',
  'processing',
]);

export const useSupplierPriceLists = (filters?: ListFeedsFilters) =>
  useQuery<SupplierPriceListsListResponse>({
    queryKey: queryKeys.supplierPriceLists.list(filters),
    queryFn: () => supplierPriceListsApi.list(filters),
  });

export const useSupplierPriceList = (id: string | undefined) =>
  useQuery<SupplierPriceListDetail>({
    queryKey: queryKeys.supplierPriceLists.detail(id ?? ''),
    queryFn: () => supplierPriceListsApi.get(id as string),
    enabled: !!id,
  });

export const useIngestSupplierPriceList = (
  options?: UseMutationOptions<IngestResponse, AxiosError, string>,
) => {
  const queryClient = useQueryClient();
  return useMutation<IngestResponse, AxiosError, string>({
    mutationFn: (id) => supplierPriceListsApi.ingest(id),
    onSuccess: (...args) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.supplierPriceLists.runs(args[1]),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.supplierPriceLists.detail(args[1]),
      });
      options?.onSuccess?.(...args);
    },
    ...options,
  });
};

/**
 * Hides the offers an incomplete-looking run did not contain (EOS-184). They
 * stop counting in the Preis-Tool, so its offer tables, anomaly feed and the
 * list's "Bester Lieferant" are refreshed too. The feed and its runs are
 * refreshed also after a refusal — a newer run may have come in meanwhile.
 */
export const useConfirmMissingRows = (feedId: string) => {
  const queryClient = useQueryClient();
  return useMutation<RunReport, AxiosError, string>({
    mutationFn: (runId) => supplierPriceListsApi.confirmMissing(feedId, runId),
    onSettled: () =>
      // Feed detail; its runs and items share the key prefix.
      queryClient.invalidateQueries({
        queryKey: queryKeys.supplierPriceLists.detail(feedId),
      }),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeys.preisTool.supplierOffersAll(),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.preisTool.supplierAnomaliesAll(),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.preisTool.itemsAll(),
        }),
      ]),
  });
};

export const useSupplierPriceListRuns = (
  id: string | undefined,
  filters?: ListRunsFilters,
) =>
  useQuery<RunsListResponse>({
    queryKey: queryKeys.supplierPriceLists.runs(id ?? '', filters),
    queryFn: () => supplierPriceListsApi.runs(id as string, filters),
    enabled: !!id,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data?.data?.length) return false;
      const hasActive = data.data.some((r) => ACTIVE_RUN_STATUSES.has(r.status));
      return hasActive ? 5_000 : false;
    },
  });

export const useSupplierPriceListItems = (
  id: string | undefined,
  filters?: ListItemsFilters,
) =>
  useQuery<ItemsListResponse>({
    queryKey: queryKeys.supplierPriceLists.items(id ?? '', filters),
    queryFn: () => supplierPriceListsApi.items(id as string, filters),
    enabled: !!id,
    placeholderData: keepPreviousData,
  });

export const useSupplierPriceItemHistory = (
  id: string | undefined,
  itemId: string | undefined,
  filters?: ListPriceHistoryFilters,
) =>
  useQuery<PriceHistoryResponse>({
    queryKey: queryKeys.supplierPriceLists.itemHistory(
      id ?? '',
      itemId ?? '',
      filters,
    ),
    queryFn: () =>
      supplierPriceListsApi.priceHistory(id as string, itemId as string, filters),
    enabled: !!id && !!itemId,
  });
